<template>
  <o-collapse
    ref="collapse"
    :visible="hasItems"
    :clickable="false"
    header-class="flex flex-row-reverse items-center w-full px-6 py-3"
    class="bg-white dark:bg-gray-900 hover:bg-gray-50 transition-all"
  >
    <template #header>
      <div class="flex-grow flex items-center justify-between ml-4">
        <div class="flex items-center space-x-3">
          <h4 class="font-medium opacity-80 capitalize">
            {{ section.title }}
          </h4>

          <o-chip size="sm" flat>
            {{ items.length }}
          </o-chip>
        </div>

        <!--<component
          :is="section.action.component"
          v-if="!isClosed && section.action"
          :label="section.action.label"
          :items="section.action.items"
          icon="add"
          :icon-size="12"
          flat
          split
          v-on="section.action.events"
        />-->

        <template v-if="section.action && !isClosed">
          <template v-if="section.action.type === 'group'">
            <UFieldGroup>
              <UButton color="neutral" variant="subtle" :label="section.action.label" v-on="section.action.events" />

              <UDropdownMenu :items="section.action.items">
                <UButton
                  color="neutral"
                  variant="subtle"
                  icon="i-lucide-chevron-down"
                />
              </UDropdownMenu>
            </UFieldGroup>
          </template>

          <template v-if="section.action.type === 'button'">
            <UButton color="neutral" variant="subtle" :label="section.action.label" v-on="section.action.events" />
          </template>
        </template>
      </div>
    </template>

    <template #toggle="{ toggle }">
      <o-button icon="accordion" size="sm" flat @click="toggle" />
    </template>

    <div class="my-2 mb-6 px-6">
      <o-table
        class="border rounded-lg bg-white dark:bg-gray-900 dark:border-gray-800"
        :data="items"
        :headers="headers"
        :limit="20"
        :tbody-class="{
          tfoot: '',
          tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-800/50',
          td: 'py-3 px-4 text-sm',
          stripe: 'bg-gray-50 dark:bg-gray-800/50'
        }"
        :thead-class="{
          thead: 'border-b dark:border-gray-800',
          tr: 'border-blue-700',
          th: 'font-normal p-4 pb-3 bg-gray-50 dark:bg-gray-800/50 opacity-80 text-sm sticky top-0',
          content: 'flex item-center'
        }"
        :tfoot-class="{
          tfoot: 'border-t dark:border-gray-800',
          tr: 'bg-gray-50',
          td: 'py-2 px-4 text-sm bg-gray-100 dark:bg-gray-800/50 border-t'
        }"
        striped
        :placeholder="`No ${section.title}`"
      >
        <template #row="{ row, tdClass }">
          <order-item
            :item="row"
            :tdClass="tdClass"
            :isClosed="isClosed"
          />
        </template>
      </o-table>
    </div>
  </o-collapse>
</template>

<script setup lang="ts">
import OrderItem from '@/components/order/elements/OrderItem.vue'

import {
  OTable,
} from '@/components/elements'

const { $i18n, $currency, $gates, $grants } = useNuxtApp()

interface Props {
  order: any
  sectionKey: string
}

const props = defineProps<Props>()

const collapse = ref<any>()

const handleAddReservations = inject('handleAddReservations') as Function
const handleAddService = inject('handleAddService') as Function
const handleAddProduct = inject('handleAddProduct') as Function
const handleAddCustomerCredit = inject('handleAddCustomerCredit') as Function
const handleAddGiftCards = inject('handleAddGiftCards') as Function
const handleCreateGiftCard = inject('handleCreateGiftCard') as Function
const handleAddPasses = inject('handleAddPasses') as Function
const handleAddCustomerCourses = inject('handleAddCustomerCourses') as Function
const handleAddCharge = inject('handleAddCharge') as Function
const handleAddDiscount = inject('handleAddDiscount') as Function
const handleAddPayment = inject('handleAddPayment') as Function
const handleRequestOnlinePayment = inject('handleRequestOnlinePayment') as Function

const canUseCourses = computed(() =>
  $gates.get('courses') && $grants.all({ course: ['view'] })
)

const sections = computed(() => ({
  reservations: {
    title: $i18n.t('general.services'),
    action: {
      type: 'group',
      label: $i18n.t('general.appointment'),
      events: { click: handleAddReservations },
      items: [
        {
          label: $i18n.t('components.order.elements.orderSection.addExistingAppointment'),
          onClick: handleAddReservations
        },
        {
          label: $i18n.t('components.order.elements.orderSection.addManualService'),
          onClick: handleAddService
        },
      ],
    },
  },
  products: {
    title: $i18n.t('general.products'),
    action: {
      type: 'group',
      label: $i18n.t('general.product'),
      events: { click: handleAddProduct },
      items: [
        {
          label: $i18n.t('order.addProducts'),
          onClick: handleAddProduct
        },
        {
          label: $i18n.t('order.addCustomerCredit'),
          onClick: handleAddCustomerCredit
        },
        {
          label: $i18n.t('order.addGiftCards'),
          onClick: handleCreateGiftCard
        },
        {
          label: $i18n.t('order.addPasses'),
          onClick: handleAddPasses
        },
        ...(canUseCourses.value
          ? [{
              label: $i18n.t('order.addCustomerCourses'),
              onClick: handleAddCustomerCourses,
            }]
          : []),
      ],
    },
  },
  charges: {
    title: $i18n.t('order.charges'),
    action: {
      type: 'button',
      label: $i18n.t('order.addCharge'),
      events: { click: handleAddCharge },
    },
  },
  discounts: {
    title: $i18n.t('general.discounts'),
    action: {
      type: 'button',
      label: 'Add discount',
      events: { click: handleAddDiscount },
    },
  },
  payments: {
    title: $i18n.t('general.payments'),
    action: {
      type: 'group',
      label: $i18n.t('general.payment'),
      events: { click: handleAddPayment },
      items: [
        {
          label: $i18n.t('order.addPayment'),
          onClick: handleAddPayment
        },
        {
          label: $i18n.t('components.order.elements.orderSection.requestOnline'),
          info: $i18n.t('components.order.elements.orderSection.requestOnlineInfo'),
          onClick: handleRequestOnlinePayment
        },
      ],
    },
  },
  paymentRequests: {
    title: $i18n.t('general.paymentRequests'),
    action: null,
  },
}))

const headers = computed(() => {
  if (props.sectionKey === 'paymentRequests') {
    return [
      { text: $i18n.t('general.item') },
      { text: '' },
      { text: $i18n.t('general.amount') },
    ]
  }

  if (isClosed.value) {
    return [
      { text: $i18n.t('general.item') },
      { text: $i18n.t('general.price') },
    ]
  }

  return [
    { text: $i18n.t('general.item') },
    { text: $i18n.t('general.actions') },
    { text: $i18n.t('general.price') },
  ]
})

const items = computed(() => {
  if (props.sectionKey === 'paymentRequests') {
    return props.order.paymentRequests || []
  }
  return props.order.data.items[props.sectionKey]
})

const total = computed(() => {
  const t = items.value.reduce((acc: number, { priceWithIncludedTaxes }: any) => acc + priceWithIncludedTaxes, 0)
  return $currency.format(t.toFixed(2))
})

const section = computed(() => (sections.value as any)[props.sectionKey])

const hasItems = computed(() => !!items.value.length)

const isClosed = computed(() => props.order.closed)

watch(hasItems, () => {
  collapse.value?.open()
})
</script>
